export type TilesetKey = 'ORIGINAL' | 'AIR_NXL' | 'AIR_VRML';

export interface BunkerSprite {
  widthFt: number;
  depthFt: number;
  spriteUrl: string;
}

export interface BunkerDef {
  type: string;
  displayName: string;
  /** Override displayName for specific tilesets (e.g. PYLON = 'Pylon' in ORIGINAL, 'Pin' in AIR). */
  tilesetDisplayNames?: Partial<Record<TilesetKey, string>>;
  tilesets: TilesetKey[];
  widthFt: number;    // fallback/placeholder dimensions (used when no sprite for active tileset)
  depthFt: number;
  sprites?: Partial<Record<TilesetKey, BunkerSprite>>;
  /**
   * Wing bunkers taper on one side. This records which edge of the LOCAL sprite
   * image the taper is on ('left' = −X axis, 'right' = +X axis).
   * When defined: (1) a visual stripe indicator is drawn on that edge, and
   * (2) the mirror rotation is kept the same as the original (taper stays on
   *     the same absolute field side rather than flipping across the centerline).
   */
  taperEdge?: 'left' | 'right';
}

/** Return the display name appropriate for the given tileset. */
export function bunkerDisplayName(def: BunkerDef, tileset: TilesetKey): string {
  return def.tilesetDisplayNames?.[tileset] ?? def.displayName;
}

// ── Real footprint dimensions from the Unity BunkerSpriteExporter manifest ────
// Values are in feet (Unity units × 3), rounded to 2dp.

const ORIG: Record<string, { widthFt: number; depthFt: number }> = {
  STARTER_BRICK:   { widthFt: 16.20, depthFt: 5.10  },
  CAN:             { widthFt: 5.98,  depthFt: 5.96  },
  BOX:             { widthFt: 4.50,  depthFt: 4.50  },
  SMALL_BOX:       { widthFt: 3.15,  depthFt: 3.15  },
  CONE:            { widthFt: 6.01,  depthFt: 5.99  },
  TORIDO:          { widthFt: 8.72,  depthFt: 7.78  },
  SMALL_TORIDO:    { widthFt: 6.98,  depthFt: 6.23  },
  CAKE:            { widthFt: 4.85,  depthFt: 4.21  },
  TALL_CAKE:       { widthFt: 4.85,  depthFt: 4.25  },
  BRICK:           { widthFt: 9.01,  depthFt: 3.00  },
  GIANT_BRICK:     { widthFt: 12.00, depthFt: 6.00  },
  PYLON:           { widthFt: 3.00,  depthFt: 3.00  },
  COLUMN:          { widthFt: 3.01,  depthFt: 3.00  },
  SNAKE_BEAM:      { widthFt: 3.05,  depthFt: 11.98 },
  TEMPLE:          { widthFt: 6.22,  depthFt: 6.22  },
  AZTEC:           { widthFt: 6.22,  depthFt: 6.22  },
  PRISM:           { widthFt: 3.30,  depthFt: 2.86  },
  GIANT_WING:      { widthFt: 6.00,  depthFt: 6.00  },
  GIANT_WING_FLAT: { widthFt: 9.00,  depthFt: 6.00  },
  A:               { widthFt: 18.00, depthFt: 4.50  },
  W:               { widthFt: 15.28, depthFt: 4.80  },
  MINI_A:          { widthFt: 9.00,  depthFt: 1.50  },
  MINI_X:          { widthFt: 6.00,  depthFt: 1.51  },
  MINI_W:          { widthFt: 9.00,  depthFt: 1.50  },
  X:               { widthFt: 17.67, depthFt: 7.69  },
  CARWASH:         { widthFt: 9.86,  depthFt: 14.33 },
  SILO:            { widthFt: 12.28, depthFt: 12.28 },
  PLUS:            { widthFt: 11.30, depthFt: 11.23 },
};

// AIR_NXL and AIR_VRML share the same mesh dimensions (different materials only).
const AIR: Record<string, { widthFt: number; depthFt: number }> = {
  STARTER_BRICK:     { widthFt: 16.20, depthFt: 5.10  },
  CAN:               { widthFt: 3.83,  depthFt: 3.83  },
  BOX:               { widthFt: 5.57,  depthFt: 5.57  },
  SMALL_BOX:         { widthFt: 3.76,  depthFt: 3.75  },
  CONE:              { widthFt: 6.01,  depthFt: 5.99  },
  TORIDO:            { widthFt: 8.20,  depthFt: 7.65  },
  SMALL_TORIDO:      { widthFt: 7.04,  depthFt: 6.48  },
  CAKE:              { widthFt: 3.65,  depthFt: 3.69  },
  TALL_CAKE:         { widthFt: 3.54,  depthFt: 3.67  },
  BRICK:             { widthFt: 9.01,  depthFt: 3.00  },
  GIANT_BRICK:       { widthFt: 10.09, depthFt: 6.57  },
  PYLON:             { widthFt: 3.40,  depthFt: 3.40  },
  COLUMN:            { widthFt: 3.40,  depthFt: 3.40  },
  SNAKE_BEAM:        { widthFt: 2.60,  depthFt: 10.69 },
  TEMPLE:            { widthFt: 5.26,  depthFt: 5.26  },
  AZTEC:             { widthFt: 5.02,  depthFt: 5.02  },
  PRISM:             { widthFt: 3.30,  depthFt: 2.86  },
  GIANT_WING:        { widthFt: 6.09,  depthFt: 6.23  },
  GIANT_WING_FLAT:   { widthFt: 8.09,  depthFt: 6.37  },
  A:                 { widthFt: 18.31, depthFt: 5.55  },
  W:                 { widthFt: 16.01, depthFt: 6.34  },
  MINI_A:            { widthFt: 8.76,  depthFt: 3.91  },
  MINI_X:            { widthFt: 5.75,  depthFt: 3.00  },
  MINI_W:            { widthFt: 7.49,  depthFt: 3.60  },
  BRICK2022:         { widthFt: 6.54,  depthFt: 3.92  },
  BRICK2022_UPRIGHT: { widthFt: 4.29,  depthFt: 3.86  },
  WING2022:          { widthFt: 6.44,  depthFt: 3.88  },
  WING2022_UPRIGHT:  { widthFt: 4.24,  depthFt: 3.82  },
  X:                 { widthFt: 17.67, depthFt: 7.69  },
  CARWASH:           { widthFt: 9.86,  depthFt: 14.33 },
  SILO:              { widthFt: 12.28, depthFt: 12.28 },
  PLUS:              { widthFt: 11.30, depthFt: 11.23 },
};

function orig(type: string): Partial<Record<TilesetKey, BunkerSprite>> | undefined {
  const d = ORIG[type];
  if (!d) return undefined;
  return { ORIGINAL: { ...d, spriteUrl: `/sprites/ORIGINAL/${type}.png` } };
}

function air(type: string): Partial<Record<TilesetKey, BunkerSprite>> | undefined {
  const d = AIR[type];
  if (!d) return undefined;
  return {
    AIR_NXL:  { ...d, spriteUrl: `/sprites/AIR_NXL/${type}.png` },
    AIR_VRML: { ...d, spriteUrl: `/sprites/AIR_VRML/${type}.png` },
  };
}

/** Sprite entries for all three tilesets. */
function allSprites(type: string): Partial<Record<TilesetKey, BunkerSprite>> | undefined {
  const o = orig(type);
  const a = air(type);
  if (!o && !a) return undefined;
  return { ...o, ...a };
}

const BOTH_AIR: TilesetKey[] = ['AIR_NXL', 'AIR_VRML'];
const ALL: TilesetKey[]      = ['ORIGINAL', 'AIR_NXL', 'AIR_VRML'];

export const BUNKER_CATALOG: BunkerDef[] = [
  // ── Shared across tilesets ────────────────────────────────────────────────
  { type: 'BOX',          displayName: 'Box',               tilesets: ALL, widthFt: 4.5,  depthFt: 4.5,  sprites: allSprites('BOX')          },
  { type: 'SMALL_BOX',    displayName: 'Small Box',         tilesets: ALL, widthFt: 3.0,  depthFt: 3.0,  sprites: allSprites('SMALL_BOX')    },
  { type: 'CAN',          displayName: 'Can',               tilesets: ALL, widthFt: 4.0,  depthFt: 4.0,  sprites: allSprites('CAN')          },
  { type: 'BRICK',        displayName: 'Brick',             tilesets: ['ORIGINAL'], widthFt: 6.0, depthFt: 3.0, sprites: orig('BRICK') },
  { type: 'GIANT_BRICK',  displayName: 'Giant Brick',       tilesets: ALL, widthFt: 9.0,  depthFt: 4.5,  sprites: allSprites('GIANT_BRICK')  },
  { type: 'TORIDO',       displayName: 'Dorito',            tilesets: ALL, widthFt: 6.0,  depthFt: 4.5,  sprites: allSprites('TORIDO')       },
  { type: 'SMALL_TORIDO', displayName: 'Small Dorito',      tilesets: ALL, widthFt: 4.5,  depthFt: 3.0,  sprites: allSprites('SMALL_TORIDO') },
  { type: 'CAKE',         displayName: 'Cake',              tilesets: ALL, widthFt: 5.0,  depthFt: 5.0,  sprites: allSprites('CAKE')         },
  { type: 'TALL_CAKE',    displayName: 'Tall Cake',         tilesets: ALL, widthFt: 5.0,  depthFt: 5.0,  sprites: allSprites('TALL_CAKE')    },
  { type: 'SNAKE_BEAM',   displayName: 'Snake',             tilesets: ALL, widthFt: 12.0, depthFt: 3.0,  sprites: allSprites('SNAKE_BEAM')   },
  { type: 'TEMPLE',       displayName: 'Temple',            tilesets: ALL, widthFt: 6.0,  depthFt: 6.0,  sprites: allSprites('TEMPLE')       },
  { type: 'AZTEC',        displayName: 'Aztec',             tilesets: ALL, widthFt: 8.0,  depthFt: 8.0,  sprites: allSprites('AZTEC')        },
  { type: 'GIANT_WING',   displayName: 'Giant Wing Upright',tilesets: ALL, widthFt: 9.0,  depthFt: 6.0,  sprites: allSprites('GIANT_WING'),   taperEdge: 'right' },
  { type: 'A',            displayName: 'A',                 tilesets: ALL, widthFt: 6.0,  depthFt: 6.0,  sprites: allSprites('A')            },
  { type: 'W',            displayName: 'W',                 tilesets: ALL, widthFt: 6.0,  depthFt: 6.0,  sprites: allSprites('W')            },
  { type: 'MINI_A',       displayName: 'Mini A',            tilesets: ALL, widthFt: 4.0,  depthFt: 4.0,  sprites: allSprites('MINI_A')       },
  { type: 'MINI_W',       displayName: 'Mini W',            tilesets: ALL, widthFt: 4.0,  depthFt: 4.0,  sprites: allSprites('MINI_W')       },
  { type: 'MINI_X',       displayName: 'Mini X',            tilesets: ALL, widthFt: 4.0,  depthFt: 4.0,  sprites: allSprites('MINI_X')       },
  { type: 'X',            displayName: 'X',                 tilesets: ALL, widthFt: 6.0,  depthFt: 6.0,  sprites: allSprites('X')            },
  { type: 'CARWASH',      displayName: 'Carwash',           tilesets: ALL, widthFt: 12.0, depthFt: 3.0,  sprites: allSprites('CARWASH')      },
  { type: 'SILO',         displayName: 'Silo',              tilesets: ALL, widthFt: 4.0,  depthFt: 4.0,  sprites: allSprites('SILO')         },

  // ── Original-only ─────────────────────────────────────────────────────────
  { type: 'CONE',   displayName: 'Cone',   tilesets: ['ORIGINAL'], widthFt: 3.0, depthFt: 3.0, sprites: orig('CONE')   },
  { type: 'COLUMN', displayName: 'Column', tilesets: ['ORIGINAL'], widthFt: 2.0, depthFt: 2.0, sprites: orig('COLUMN') },
  { type: 'PRISM',  displayName: 'Prism',  tilesets: ['ORIGINAL'], widthFt: 6.0, depthFt: 3.0, sprites: orig('PRISM')  },

  // PYLON = 'Pylon' in Original; 'Pin' in Airball (both PYLON and COLUMN slots map to Pin NXL)
  {
    type: 'PYLON', displayName: 'Pylon',
    tilesetDisplayNames: { AIR_NXL: 'Pin', AIR_VRML: 'Pin' },
    tilesets: ALL, widthFt: 2.0, depthFt: 2.0,
    sprites: allSprites('PYLON'),
  },

  // GIANT_WING_FLAT = 'Giant Wing' in Airball (the side/flat variant)
  {
    type: 'GIANT_WING_FLAT', displayName: 'Giant Wing',
    tilesets: ALL, widthFt: 9.0, depthFt: 3.0,
    sprites: allSprites('GIANT_WING_FLAT'),
    taperEdge: 'left', // 270° default → local -X faces canvas-down
  },

  // Plus is ORIGINAL-only in the old set; available in Airball via bunker-star2026
  {
    type: 'PLUS', displayName: 'Plus',
    tilesets: ALL, widthFt: 6.0, depthFt: 6.0,
    sprites: allSprites('PLUS'),
  },

  // ── Airball 2022 exclusive ─────────────────────────────────────────────────
  { type: 'BRICK2022',         displayName: 'Brick',         tilesets: BOTH_AIR, widthFt: 6.75, depthFt: 3.75, sprites: air('BRICK2022')         },
  { type: 'BRICK2022_UPRIGHT', displayName: 'Brick Upright', tilesets: BOTH_AIR, widthFt: 3.75, depthFt: 6.75, sprites: air('BRICK2022_UPRIGHT') },
  { type: 'WING2022',          displayName: 'Wing',          tilesets: BOTH_AIR, widthFt: 9.0,  depthFt: 4.5,  sprites: air('WING2022'),          taperEdge: 'right' },
  { type: 'WING2022_UPRIGHT',  displayName: 'Wing Upright',  tilesets: BOTH_AIR, widthFt: 4.5,  depthFt: 9.0,  sprites: air('WING2022_UPRIGHT'),  taperEdge: 'left'  },
];

export function getBunkersForTileset(tileset: TilesetKey): BunkerDef[] {
  return BUNKER_CATALOG.filter(b => b.tilesets.includes(tileset));
}

export const TILESET_LABELS: Record<TilesetKey, string> = {
  ORIGINAL: 'Original',
  AIR_NXL:  'Airball NXL',
  AIR_VRML: 'Airball VRML',
};
